// React
import PropTypes from "prop-types";
import { useContext, useEffect, useRef, useState } from "react";

// @mui material components
import {
  Card,
  CircularProgress,
  Fade,
  Grid,
  Icon,
  InputLabel,
  Menu,
  MenuItem,
  Modal,
  Tooltip,
} from "@mui/material";

import { gql, useMutation } from "@apollo/client";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import { MessageManager } from "context";
import ModalNuevoProducto from "./ModalNuevoProducto";
import DataTable from "components/Tables/DataTable";
import SinDatos from "components/Carteles/SinDatos";
import dataProductos from "./data/dataProductos";
import { getColor } from "utils/colors";
import dayjs from "dayjs";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90%",
  width: { xs: "90%", sm: "80%", xxl: "70%" },
  overflowY: "auto",
  p: 3,
};

export default function ModalGuia({ open, handleClose, refetch, selectedGuia, tipo }) {
  const [guia, setGuia] = useState();
  const [listadoProductos, setListadoProductos] = useState([]);
  const inputRef = useRef();
  const [anchorEl, setAnchorEl] = useState(null);
  const [openMenu, setOpenMenu] = useState(false);
  const [file, setFile] = useState();
  const { handleSnackbar } = useContext(MessageManager);
  const [modalProducto, setModalProducto] = useState(false);
  const [rows, setRows] = useState([]);
  const { columns, getRows } = dataProductos;
  const [selectedProducto, setSelectedProducto] = useState();

  useEffect(() => {
    if (!open) {
      setGuia();
      setListadoProductos([]);
    }
  }, [open]);

  const handleChanges = (e) => {
    setGuia({
      ...guia,
      [e.target.name]: e.target.value,
    });
  };
  const [addGuiaTransito, { loading: loadingSave }] = useMutation(
    gql`
      mutation addGuiaTransito($input: GuiaTransitoInput!) {
        addGuiaTransito(input: $input) {
          id
        }
      }
    `
  );

  const handleSave = () => {
    addGuiaTransito({
      variables: {
        input: {
          ...guia,
          detalles: listadoProductos,
          file: file,
        },
      },
    })
      .then(() => {
        refetch();
        handleClose();
        handleSnackbar("Guía guardada correctamente");
      })
      .catch((e) => {
        handleSnackbar("Error al guardar la guía", "error");
      });
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setOpenMenu(false);
  };

  const renderMenu = (
    <Menu id="simple-menu" anchorEl={anchorEl} open={Boolean(openMenu)} onClose={handleCloseMenu}>
      <MenuItem
        onClick={() => {
          setListadoProductos(listadoProductos.filter((item) => item !== selectedProducto));
          handleCloseMenu();
        }}
      >
        <SoftBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            width: "100%",
          }}
        >
          <SoftBox>
            <SoftTypography variant="p">Borrar</SoftTypography>
          </SoftBox>
          <SoftBox>
            <Icon
              fontSize="small"
              sx={{
                verticalAlign: "middle",
                color: getColor("gobPrimary"),
              }}
            >
              delete
            </Icon>
          </SoftBox>
        </SoftBox>
      </MenuItem>
    </Menu>
  );

  const handleOpenMenu = (event, item) => {
    setAnchorEl(event.currentTarget);
    setOpenMenu(true);
    setSelectedProducto(item);
  };

  useEffect(() => {
    if (listadoProductos) {
      setRows(getRows(listadoProductos, handleOpenMenu));
    }
  }, [listadoProductos]);

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card sx={style}>
          <SoftBox>
            <SoftBox display="flex" justifyContent="space-between">
              <SoftBox>
                <SoftTypography variant="h6">
                  {selectedGuia ? `Editar guía de tránsito` : `Nueva guía de tránsito`}
                </SoftTypography>
              </SoftBox>
              <SoftBox display="flex" justifyContent="space-between">
                <SoftBox>
                  <Tooltip title="Cerrar" placement="top">
                    <Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer" }}>
                      close
                    </Icon>
                  </Tooltip>
                </SoftBox>
              </SoftBox>
            </SoftBox>
            <SoftBox py={3}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <InputLabel htmlFor="username">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Domicilio del solicitante
                      <SoftTypography variant="caption" fontWeight="bold" color="primary">
                        &nbsp;*
                      </SoftTypography>
                    </SoftTypography>
                  </InputLabel>
                  <SoftInput
                    label="domicilioSolicitante"
                    placeholder="Ingrese su domicilio"
                    type="text"
                    name="domicilioSolicitante"
                    value={guia?.domicilioSolicitante || ""}
                    onChange={handleChanges}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <InputLabel htmlFor="username">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Productos originarios de:
                      <SoftTypography variant="caption" fontWeight="bold" color="primary">
                        &nbsp;*
                      </SoftTypography>
                    </SoftTypography>
                  </InputLabel>
                  <SoftInput
                    label="originariosDe"
                    placeholder="Ingrese el origen de los productos"
                    type="text"
                    name="originariosDe"
                    value={guia?.originariosDe || ""}
                    onChange={handleChanges}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <InputLabel htmlFor="medioTransporte">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Medio de transporte (Modelo - Dominio)
                    </SoftTypography>
                    <SoftTypography variant="caption" fontWeight="bold" color="primary">
                      &nbsp;*
                    </SoftTypography>
                  </InputLabel>
                  <SoftInput
                    label="medioTransporte"
                    placeholder="Ingrese el medio de transporte"
                    type="text"
                    name="medioTransporte"
                    value={guia?.medioTransporte || ""}
                    onChange={handleChanges}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <InputLabel htmlFor="destino">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Nombre firma o persona jurídica (destino)
                      <SoftTypography variant="caption" fontWeight="bold" color="primary">
                        &nbsp;*
                      </SoftTypography>
                    </SoftTypography>
                  </InputLabel>
                  <SoftInput
                    label="destino"
                    placeholder="Ingrese nombre de quien recibe"
                    type="text"
                    name="destino"
                    value={guia?.destino || ""}
                    onChange={handleChanges}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <InputLabel htmlFor="domicilioDestino">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Domicilio firma o persona jurídica (destino)
                      <SoftTypography variant="caption" fontWeight="bold" color="primary">
                        &nbsp;*
                      </SoftTypography>
                    </SoftTypography>
                  </InputLabel>
                  <SoftInput
                    label="domicilioDestino"
                    placeholder="Ingrese el domicilio destino"
                    type="text"
                    name="domicilioDestino"
                    value={guia?.domicilioDestino || ""}
                    onChange={handleChanges}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <InputLabel htmlFor="fechaExpedicion">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Fecha que requiere la guía
                    </SoftTypography>
                    <SoftTypography variant="caption" fontWeight="bold" color="primary">
                      &nbsp;*
                    </SoftTypography>
                  </InputLabel>
                  <SoftInput
                    label="fechaExpedicion"
                    type="date"
                    name="fechaExpedicion"
                    value={guia?.fechaExpedicion || ""}
                    onChange={handleChanges}
                    error={guia?.fechaExpedicion && dayjs(guia?.fechaExpedicion).isBefore(dayjs())}
                  />
                </Grid>
                <Grid item xs={12}>
                  <InputLabel htmlFor="comentario">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Comentario
                    </SoftTypography>
                  </InputLabel>
                  <SoftInput
                    label="comentario"
                    placeholder="Comentario adicional"
                    type="text"
                    name="comentario"
                    value={guia?.comentario || ""}
                    onChange={handleChanges}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} py={2}>
                <Grid item xs={12} display="flex" justifyContent="space-between">
                  <SoftTypography variant="h6">Productos</SoftTypography>
                  <Tooltip
                    title="Agregar producto"
                    placement="top"
                    arrow
                    sx={{
                      cursor: "pointer",
                    }}
                  >
                    <SoftButton
                      color="primary"
                      onClick={() => setModalProducto(true)}
                      variant="contained"
                      size="small"
                    >
                      <Icon>add</Icon>
                      &nbsp;
                      <SoftTypography
                        variant="caption"
                        fontWeight="bold"
                        sx={{
                          display: { xs: "none", sm: "block" },
                        }}
                        color="white"
                      >
                        NUEVO PRODUCTO
                      </SoftTypography>
                    </SoftButton>
                  </Tooltip>
                </Grid>
              </Grid>
              <SoftBox
                sx={{
                  "& .MuiTableRow-root:not(:last-child)": {
                    "& td": {
                      borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                        `${borderWidth[1]} solid ${borderColor}`,
                    },
                  },
                }}
              >
                {listadoProductos?.length > 0 ? (
                  <DataTable columns={columns} rows={rows} />
                ) : (
                  <SinDatos />
                )}
              </SoftBox>
              {renderMenu}
            </SoftBox>
            <SoftBox
              sx={{
                lineHeight: 0.7,
              }}
              pb={1}
            >
              <SoftTypography
                variant="caption"
                sx={{
                  fontSize: 15,
                }}
              >
                Debe realizar una transferencia bancaria por <strong>$9.900</strong> pesos
                <br />
                <strong>Banco Nación</strong>&nbsp;CBU:&nbsp;
                <strong>0110478720047801141727</strong>
              </SoftTypography>
            </SoftBox>
            <SoftBox
              display={{
                xs: "flex-row",
                sm: "flex",
              }}
              justifyContent="space-between"
              alignItems="center"
            >
              <Grid
                display={{
                  xs: "block",
                  sm: "flex",
                }}
                justifyContent="end"
                alignItems="center"
              >
                <SoftButton
                  color={file ? "success" : "gobSecondary"}
                  fullWidth={{
                    xs: true,
                    sm: false,
                  }}
                  onClick={() => inputRef.current.click()}
                >
                  {file
                    ? file.name.length > 40
                      ? `${file.name.substring(0, 40)}...${file.name.substring(
                          file.name.length - 4
                        )}`
                      : file.name
                    : "Subir comprobante de pago"}
                  &nbsp;
                  <Icon>upload</Icon>
                </SoftButton>
              </Grid>
              <SoftBox
                display={{
                  xs: "flex-row",
                  sm: "flex",
                }}
                justifyContent="end"
                alignItems="center"
              >
                <SoftBox
                  mr={{
                    xs: 0,
                    sm: 2,
                  }}
                  mt={{
                    xs: 2,
                    sm: 0,
                  }}
                >
                  <SoftButton
                    color="primary"
                    onClick={handleClose}
                    fullWidth={{
                      xs: true,
                      sm: false,
                    }}
                    disabled={loadingSave}
                  >
                    <Icon sx={{ fontWeight: "light" }}>cancel</Icon>
                    &nbsp;Cancelar
                  </SoftButton>
                </SoftBox>
                <SoftBox
                  mt={{
                    xs: 2,
                    sm: 0,
                  }}
                >
                  <SoftButton
                    color="gobSecondary"
                    onClick={handleSave}
                    fullWidth={{
                      xs: true,
                      sm: false,
                    }}
                    disabled={
                      loadingSave ||
                      !guia?.originariosDe ||
                      !guia?.domicilioSolicitante ||
                      !guia?.destino ||
                      !guia?.domicilioDestino ||
                      !guia?.medioTransporte ||
                      !guia?.fechaExpedicion ||
                      !file ||
                      !listadoProductos.length > 0
                    }
                  >
                    {loadingSave ? (
                      <CircularProgress
                        size={15}
                        sx={{
                          marginRight: 0.5,
                        }}
                        color="inherit"
                      />
                    ) : (
                      <Icon sx={{ fontWeight: "light" }}>save</Icon>
                    )}
                    &nbsp;Guardar
                  </SoftButton>
                </SoftBox>
              </SoftBox>
            </SoftBox>
            <input
              ref={inputRef}
              type="file"
              style={{ display: "none" }}
              accept=".pdf, .jpg, .jpeg, .png"
              onChange={(e) => {
                if (e.target.files[0] && e.target.files[0].size < 5000000) {
                  setFile(e.target.files[0]);
                } else {
                  handleSnackbar("El archivo debe pesar menos de 5MB", "error");
                }
              }}
            />
            <ModalNuevoProducto
              open={modalProducto}
              handleClose={() => setModalProducto(false)}
              setListadoProductos={setListadoProductos}
              listadoProductos={listadoProductos}
            />
          </SoftBox>
        </Card>
      </Fade>
    </Modal>
  );
}

ModalGuia.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  refetch: PropTypes.func,
  selectedGuia: PropTypes.object,
  tipo: PropTypes.string,
};
